import { generatePath, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import PageHeader from "@/shared/components/layout/dashboard-layout/page-header.tsx";
import PageContent from "@/shared/components/layout/dashboard-layout/page-content.tsx";
import SkeletonPage from "@/shared/components/skeleton/page/skeleton-page.tsx";
import { Machine } from "@/machines/types/machine.ts";
import { TruckEventOverview } from "@/machines/types/event.ts";
import { useTimelineContext } from "@/machines/context/timeline-context.tsx";
import { useSelectedTime } from "@/machines/context/cursor-store.ts";
import { ApiResponse } from "@/shared/types/api.ts";
import { useDateTimeRangeStore } from "@/shared/stores/useDateTimeRangeStore.ts";
import { useOrganizationStore } from "@/shared/stores/useOrganizationStore.ts";
import { useGetOrganizationById } from "@/auth/api/queries/user.queries.ts";
import { useGetLocationById } from "@/locations/api/queries/location.queries.ts";
import { MACHINES_URL } from "@/machines/routes.ts";
import { useSetBreadcrumbs } from "@/shared/contexts/breadcrumb-context";
import { useCallback, useMemo } from "react";
import MachineDetailKpiSection from "./sections/machine-detail-kpi-section";
import MachineDetailVideoMapTimelineSection from "./sections/machine-detail-video-map-timeline-section";
import MachineDetailTablesSection from "./sections/machine-detail-tables-section";
import MachinesLiveMain from "@/machines/components/common/machines-live/machines-live-main";
import { useSeekToTimestampParam } from "@/machines/components/hooks/use-seek-to-timestamp-param";

interface MachineDetailContentProps {
  machine: ApiResponse<Machine> | undefined;
  status: "pending" | "error" | "success";
  isTimelineLoading: boolean;
  isTimelineEmpty: boolean;
  truckEventsOverview: ApiResponse<TruckEventOverview> | undefined;
}

const MachinesDetailContent = ({machine, status, isTimelineLoading, isTimelineEmpty, truckEventsOverview}: MachineDetailContentProps) => {
  const { t } = useTranslation("machines");
  const { id } = useParams();
  const { value } = useDateTimeRangeStore();
  const { selectedOrganizationId } = useOrganizationStore();


  const { setSelectedTime } = useTimelineContext();
  const selectedTime = useSelectedTime();

  // Jump to a specific event when arriving via `/machine/:id?ts=<epochMs>`
  // (e.g. from the ML-export review "Open in machine timeline" link).
  useSeekToTimestampParam(isTimelineLoading);

  const graphData = truckEventsOverview?.data?.graphData;
  const overviewMetadata = truckEventsOverview?.data?.metadata;

  const handleChartBarClick = useCallback((index: number) => {
    if (!graphData || !graphData[index]) return;
    const startTime = new Date(graphData[index].truckLoading.startTime).getTime();
    setSelectedTime(startTime, "external");
  }, [graphData, setSelectedTime]);

  // Find the chart bar index closest to the current selectedTime
  const chartActiveIndex = useMemo(() => {
    if (selectedTime === null || !graphData || graphData.length === 0) return null;

    let closestIndex = -1;
    let minDist = Infinity;

    for (let i = 0; i < graphData.length; i++) {
      const start = new Date(graphData[i].truckLoading.startTime).getTime();
      const end = new Date(graphData[i].truckLoading.endTime).getTime();

      // If selectedTime is within this event, exact match
      if (selectedTime >= start && selectedTime <= end) {
        return i;
      }

      // Otherwise find closest
      const dist = Math.min(Math.abs(selectedTime - start), Math.abs(selectedTime - end));
      if (dist < minDist) {
        minDist = dist;
        closestIndex = i;
      }
    }

    // Only show if reasonably close (within 5 minutes)
    return minDist < 5 * 60 * 1000 ? closestIndex : null;
  }, [selectedTime, graphData]);

  const device_mxid = machine?.data?.device_mxid ?? "";
  const locationId = machine?.data?.location?.id ?? machine?.data?.location_id;
  const machineId = id ? parseInt(id) : 0;

  const { data: orgData, isLoading: isOrgLoading } = useGetOrganizationById(selectedOrganizationId!);
  const { data: locationData, isLoading: isLocationLoading } = useGetLocationById(locationId || 0);

  const isMachineLoading = status === "pending";

  // Set breadcrumbs using the hook
  useSetBreadcrumbs([
    {
      path: MACHINES_URL.LOCATION,
      breadcrumb: orgData?.data?.name || t("breadcrumbs.organization"),
      type: "organizations",
      currentId: selectedOrganizationId!,
      isLoading: isOrgLoading
    },
    {
      path: generatePath(MACHINES_URL.LOCATION_DETAIL, { id: locationId?.toString() ?? "" }),
      breadcrumb: locationData?.data?.name || machine?.data?.location?.name || t("breadcrumbs.locationFallback"),
      type: "locations",
      currentId: locationId,
      isLoading: isLocationLoading || isMachineLoading
    },
    {
      path: generatePath(MACHINES_URL.MACHINE_DETAIL, { id: id ?? "" }),
      breadcrumb: machine?.data?.name || t("breadcrumbs.machine", { id }),
      type: "machines",
      currentId: machineId,
      locationId: locationId,
      isLoading: isMachineLoading
    },
  ]);

  if (status === "pending") {
    return <SkeletonPage />
  }

  if (!machine?.data) {
    return t("detail.empty")
  }

  return (
    <div className="flex flex-col h-full">
      <PageHeader />

      {/* KPI Section */}
      <MachineDetailKpiSection
        machine={machine.data}
        truckEventsOverview={truckEventsOverview?.data}
      />

      <div className="flex-1">
        <PageContent>
          <div className="flex flex-col gap-4">
            {/* Video, Map & Timeline Section */}
            <MachineDetailVideoMapTimelineSection
              machine={machine.data}
              selectedTime={selectedTime}
              machineId={id}
              isTimelineLoading={isTimelineLoading}
              isTimelineEmpty={isTimelineEmpty}
            />

            {/* Truck Loading Chart */}
            {graphData && graphData.length > 0 && overviewMetadata && (
              <div className="w-full rounded-2xl bg-sidebar">
                <MachinesLiveMain
                  metadata={overviewMetadata}
                  chartData={graphData}
                  machine={machine?.data}
                  height={200}
                  onBarClick={handleChartBarClick}
                  activeIndex={chartActiveIndex}
                />
              </div>
            )}

            {/* Tables Section */}
            <MachineDetailTablesSection
              machine={machine.data}
              setSelectedTime={setSelectedTime}
            />
          </div>
        </PageContent>
      </div>
    </div>
  )
}

export default MachinesDetailContent;